import React from 'react';
import { createPortal } from 'react-dom';
import { Trade } from '../ActiveTradesPage';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import './PackingSlipModal.css';

interface Props {
  trade: Trade;
  isOpen: boolean;
  onClose: () => void;
}

export default function PackingSlipModal({ trade, isOpen, onClose }: Props) {
  if (!isOpen) return null;

  const copyAddressToClipboard = async () => {
    let addressLines = [
      trade.shippingAddress.name,
      trade.shippingAddress.street
    ];
    
    // Add street2 if it exists
    if (trade.shippingAddress.street2) {
      addressLines.push(trade.shippingAddress.street2);
    }
    
    addressLines.push(
      `${trade.shippingAddress.city}, ${trade.shippingAddress.state} ${trade.shippingAddress.zipCode}`,
      trade.shippingAddress.country
    );
    
    const address = addressLines.join('\n');
    
    try {
      await navigator.clipboard.writeText(address);
      console.log('Address copied to clipboard');
      // Could add a toast notification here if available
    } catch (err) {
      console.error('Failed to copy address to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = address;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      console.log('Address copied to clipboard (fallback)');
    }
  };

  const printPackingSlip = () => {
    // Hide all other content during print
    const body = document.body;
    const allElements = body.querySelectorAll('*:not(.modal-overlay):not(.packing-slip-modal):not(.packing-slip-modal *)');
    
    // Add print-hide class to all non-modal elements
    allElements.forEach(element => {
      if (!element.closest('.modal-overlay')) {
        element.classList.add('print-hide');
      }
    });
    
    // Trigger print
    window.print();
    
    // Clean up - remove print-hide class after printing
    setTimeout(() => {
      allElements.forEach(element => {
        element.classList.remove('print-hide');
      });
    }, 1000);
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="packing-slip-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Packing Slip - Trade #{trade.tradeNumber}</h2>
          <button className="close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="packing-slip-content">
          <div className="slip-section">
            <h3>Ship To:</h3>
            <div className="shipping-address">
              <div className="address-line">{trade.shippingAddress.name}</div>
              <div className="address-line">{trade.shippingAddress.street}</div>
              {trade.shippingAddress.street2 && (
                <div className="address-line">{trade.shippingAddress.street2}</div>
              )}
              <div className="address-line">
                {trade.shippingAddress.city}, {trade.shippingAddress.state} {trade.shippingAddress.zipCode}
              </div>
              <div className="address-line">{trade.shippingAddress.country}</div>
            </div>
            <div className="address-actions">
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/icon-print.svg`}
                    alt="Print"
                    style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Print Packing Slip"
                color="purple"
                onPress={printPackingSlip}
              />
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/icon-copy.svg`}
                    alt="Copy"
                    style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Copy Address"
                color="dark-grey"
                onPress={copyAddressToClipboard}
              />
            </div>
          </div>

          <div className="slip-section">
            <h3>Package Contents:</h3>
            <div className="contents-table">
              <div className="contents-header">
                <span className="col-qty">Qty</span>
                <span className="col-name">Card Name</span>
                <span className="col-set">Set</span>
                <span className="col-condition">Condition</span>
                <span className="col-language">Lang</span>
              </div>
              {trade.items.map((item, index) => (
                <div key={index} className="contents-row">
                  <span className="col-qty">{item.qty}</span>
                  <span className="col-name">{item.name}</span>
                  <span className="col-set">{item.set}</span>
                  <span className="col-condition">{item.condition}</span>
                  <span className="col-language">{item.language}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="slip-section">
            <div className="slip-footer">
              <div className="trade-info-small">
                <span>Trade #{trade.tradeNumber}</span>
                <span>Prepared: {formatDate()}</span>
                <span>Total Items: {trade.items.reduce((sum, item) => sum + item.qty, 0)}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}